import React, { useMemo, useState } from 'react';
import { Award, AlertTriangle, CheckCircle2, BookOpen, TrendingUp, ChevronDown, ChevronRight, Brain } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { useQualityWorkspaceState } from './useQualityWorkspaceState';
import { scoreQuestion } from '../../../governance/questionQualityEngine';
import { CognitiveLevelChart } from './CognitiveLevelChart';
interface DepthAnalysis {
  questionId: string;
  module: string;
  tier: number;
  group: number;
  clarityScore: number;
  depthScore: number;
  distractorScore: number;
  totalScore: number;
  clarityIssues: string[];
  depthIssues: string[];
  distractorIssues: string[];
  explanationText?: string;
}
export function DidacticDepthPanel() {
  const {
    filteredRecords
  } = useQualityWorkspaceState();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'total' | 'clarity' | 'depth'>('total');
  // Step 7 fix: call scoreQuestion once per question — it already runs
  // assessExplanationDepth and assessDistractorDepth internally.
  // Previously called scoreQuestion + assessExplanationDepth + assessDistractorDepth = 3× per question.
  const analysis = useMemo(() => {
    const results: DepthAnalysis[] = filteredRecords.map(r => {
      const qualityScore = scoreQuestion(r.question);
      return {
        questionId: r.question.id,
        module: r.question.module,
        tier: r.question.tier,
        group: r.question.group,
        clarityScore: qualityScore.dimensions.explanationClarity.score,
        depthScore: qualityScore.dimensions.didacticDepth.score,
        distractorScore: qualityScore.dimensions.distractors.score,
        totalScore: qualityScore.dimensions.explanationClarity.score + qualityScore.dimensions.didacticDepth.score + qualityScore.dimensions.distractors.score,
        clarityIssues: qualityScore.dimensions.explanationClarity.issues,
        depthIssues: qualityScore.dimensions.didacticDepth.issues,
        distractorIssues: qualityScore.dimensions.distractors.issues,
        explanationText: r.question.feedback?.explanationText
      };
    });
    return results.sort((a, b) => {
      if (sortBy === 'clarity') return a.clarityScore - b.clarityScore;
      if (sortBy === 'depth') return a.depthScore - b.depthScore;
      return a.totalScore - b.totalScore;
    });
  }, [filteredRecords, sortBy]);
  const stats = useMemo(() => {
    if (analysis.length === 0) return {
      avgClarity: 0,
      avgDepth: 0,
      avgDistractor: 0,
      avgTotal: 0
    };
    return {
      avgClarity: Math.round(analysis.reduce((sum, a) => sum + a.clarityScore, 0) / analysis.length),
      avgDepth: Math.round(analysis.reduce((sum, a) => sum + a.depthScore, 0) / analysis.length),
      avgDistractor: Math.round(analysis.reduce((sum, a) => sum + a.distractorScore, 0) / analysis.length),
      avgTotal: Math.round(analysis.reduce((sum, a) => sum + a.totalScore, 0) / analysis.length)
    };
  }, [analysis]);
  const questions = useMemo(() => filteredRecords.map(r => r.question), [filteredRecords]);
  return <div className="space-y-6" data-id="element-2048">
      {/* Cognitive Level Chart (Step 7) */}
      <CognitiveLevelChart questions={questions} data-id="element-2049" />

      {/* Header KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2050">
        <Card className="p-4 bg-blue-50 border-blue-200" data-id="element-2051">
          <div className="text-2xl font-black text-blue-700" data-id="element-2052">
            {stats.avgClarity}
          </div>
          <div className="text-xs font-bold text-blue-600 uppercase tracking-wider" data-id="element-2053">
            Avg Clarity
          </div>
          <div className="text-[10px] text-blue-500 mt-0.5" data-id="element-2054">20pts max</div>
        </Card>
        <Card className="p-4 bg-purple-50 border-purple-200" data-id="element-2055">
          <div className="text-2xl font-black text-purple-700" data-id="element-2056">
            {stats.avgDepth}
          </div>
          <div className="text-xs font-bold text-purple-600 uppercase tracking-wider" data-id="element-2057">
            Avg Depth
          </div>
          <div className="text-[10px] text-purple-500 mt-0.5" data-id="element-2058">30pts max</div>
        </Card>
        <Card className="p-4 bg-amber-50 border-amber-200" data-id="element-2059">
          <div className="text-2xl font-black text-amber-700" data-id="element-2060">
            {stats.avgDistractor}
          </div>
          <div className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-2061">
            Avg Distractor
          </div>
          <div className="text-[10px] text-amber-500 mt-0.5" data-id="element-2062">25pts max</div>
        </Card>
        <Card className="p-4 bg-indigo-50 border-indigo-200" data-id="element-2063">
          <div className="text-2xl font-black text-indigo-700" data-id="element-2064">
            {stats.avgTotal}
          </div>
          <div className="text-xs font-bold text-indigo-600 uppercase tracking-wider" data-id="element-2065">
            Avg Total
          </div>
          <div className="text-[10px] text-indigo-500 mt-0.5" data-id="element-2066">75pts max</div>
        </Card>
      </div>

      {/* Sort Controls */}
      <div className="flex items-center justify-between" data-id="element-2067">
        <h3 className="text-lg font-bold text-slate-900" data-id="element-2068">
          Question Analysis ({analysis.length})
        </h3>
        <div className="flex gap-2" data-id="element-2069">
          {(['total', 'clarity', 'depth'] as const).map(s => <button key={s} onClick={() => setSortBy(s)} className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${sortBy === s ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'}`} data-id="element-2070">
              Sort by {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>)}
        </div>
      </div>

      {/* Question List */}
      {analysis.length === 0 ? <Card className="p-12 text-center" data-id="element-2071">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" data-id="element-2072" />
          <h3 className="text-lg font-bold text-slate-900" data-id="element-2073">
            No questions match filters
          </h3>
          <p className="text-slate-500 mt-1" data-id="element-2074">
            Adjust your filters to see didactic depth analysis.
          </p>
        </Card> : <div className="space-y-3" data-id="element-2075">
          {analysis.map(item => <Card key={item.questionId} className={`transition-all ${expandedId === item.questionId ? 'ring-2 ring-indigo-500 shadow-md' : 'hover:shadow-sm'}`} data-id="element-2076">
              <div className="p-4 cursor-pointer" onClick={() => setExpandedId(expandedId === item.questionId ? null : item.questionId)} data-id="element-2077">
                <div className="flex items-start justify-between mb-3" data-id="element-2078">
                  <div className="flex items-center gap-3" data-id="element-2079">
                    <code className="text-sm font-bold text-slate-900 bg-slate-100 px-2 py-1 rounded" data-id="element-2080">
                      {item.questionId}
                    </code>
                    <Badge variant="outline" size="sm" data-id="element-2081">
                      {item.module}
                    </Badge>
                    <Badge variant="outline" size="sm" data-id="element-2082">
                      T{item.tier} · G{item.group}
                    </Badge>
                  </div>
                  {expandedId === item.questionId ? <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" data-id="element-2083" /> : <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" data-id="element-2084" />}
                </div>
                <div className="grid grid-cols-4 gap-3" data-id="element-2085">
                  <ScoreBar label="Clarity" score={item.clarityScore} max={20} color="blue" data-id="element-2086" />
                  <ScoreBar label="Depth" score={item.depthScore} max={30} color="purple" data-id="element-2087" />
                  <ScoreBar label="Distractor" score={item.distractorScore} max={25} color="amber" data-id="element-2088" />
                  <ScoreBar label="Total" score={item.totalScore} max={75} color="indigo" data-id="element-2089" />
                </div>
              </div>

              {expandedId === item.questionId && <div className="border-t border-slate-100 p-4 bg-slate-50/50 space-y-4" data-id="element-2090">
                  {item.explanationText && <div className="bg-white rounded-lg p-3 border border-slate-200" data-id="element-2091">
                      <div className="flex items-center gap-2 mb-2" data-id="element-2092">
                        <BookOpen className="w-4 h-4 text-slate-400" data-id="element-2093" />
                        <span className="text-xs font-bold text-slate-500 uppercase" data-id="element-2094">
                          Explanation Text
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed" data-id="element-2095">
                        {item.explanationText}
                      </p>
                    </div>}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-2096">
                    <IssueCard title="Clarity Issues" issues={item.clarityIssues} color="blue" icon={BookOpen} data-id="element-2097" />
                    <IssueCard title="Depth Issues" issues={item.depthIssues} color="purple" icon={TrendingUp} data-id="element-2098" />
                    <IssueCard title="Distractor Issues" issues={item.distractorIssues} color="amber" icon={AlertTriangle} data-id="element-2099" />
                  </div>
                </div>}
            </Card>)}
        </div>}
    </div>;
}
function ScoreBar({
  label,
  score,
  max,
  color
}: {
  label: string;
  score: number;
  max: number;
  color: 'blue' | 'purple' | 'amber' | 'indigo';
}) {
  const percent = Math.round(score / max * 100);
  const colors = {
    blue: {
      bg: 'bg-blue-500',
      text: 'text-blue-700'
    },
    purple: {
      bg: 'bg-purple-500',
      text: 'text-purple-700'
    },
    amber: {
      bg: 'bg-amber-500',
      text: 'text-amber-700'
    },
    indigo: {
      bg: 'bg-indigo-500',
      text: 'text-indigo-700'
    }
  };
  return <div data-id="element-2100">
      <div className="flex items-center justify-between mb-1" data-id="element-2101">
        <span className="text-xs font-semibold text-slate-600" data-id="element-2102">{label}</span>
        <span className={`text-xs font-bold ${colors[color].text}`} data-id="element-2103">
          {score}/{max}
        </span>
      </div>
      <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden" data-id="element-2104">
        <div className={`h-full ${colors[color].bg} transition-all duration-300`} style={{
        width: `${percent}%`
      }} data-id="element-2105" />
      </div>
    </div>;
}
function IssueCard({
  title,
  issues,
  color,
  icon: Icon
}: {
  title: string;
  issues: string[];
  color: 'blue' | 'purple' | 'amber';
  icon: any;
}) {
  const colors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700'
  };
  return <div className={`rounded-lg border p-3 ${colors[color]}`} data-id="element-2106">
      <div className="flex items-center gap-2 mb-2" data-id="element-2107">
        <Icon className="w-4 h-4" data-id="element-2108" />
        <span className="text-xs font-bold uppercase tracking-wider" data-id="element-2109">
          {title}
        </span>
      </div>
      {issues.length === 0 ? <div className="flex items-center gap-1.5 text-xs" data-id="element-2110">
          <CheckCircle2 className="w-3.5 h-3.5" data-id="element-2111" />
          <span data-id="element-2112">No issues</span>
        </div> : <ul className="space-y-1" data-id="element-2113">
          {issues.map((issue, idx) => <li key={idx} className="text-xs flex items-start gap-1.5" data-id="element-2114">
              <span className="mt-1 w-1 h-1 rounded-full bg-current shrink-0" data-id="element-2115" />
              <span className="flex-1" data-id="element-2116">{issue}</span>
            </li>)}
        </ul>}
    </div>;
}